import { debounce } from 'debounce';

import { CancelTokenSource } from 'axios';
import { API } from './api';

import { Product } from '../entities/Product';
import { nodeTeaser } from '../templates/nodeTeaser';

export class Searcher {
    private static cancelTokenSoruces: CancelTokenSource[] = [];

    public static create(input: HTMLInputElement): void {
        const [container, inputContainer, searchResults, searchLoading] = this._createHTML();

        input.parentElement?.insertBefore(container, input);
        inputContainer.appendChild(input);

        if (input.classList.contains('searcher')) {
            input.classList.remove('searcher');
            input.classList.add('searcher__input');
        } else {
            input.classList.add('searcher__input');
        }

        this._addEventListeners(input, container, searchResults, searchLoading);
    }

    private static _createHTML(): HTMLElement[] {
        const container = document.createElement('div');
        container.tabIndex = 0;
        container.classList.add('searcher');

        const inputContainer = document.createElement('div');
        inputContainer.classList.add('searcher__input-container');

        const searchResults = document.createElement('div');
        searchResults.classList.add('searcher__results');

        const searchLoading = document.createElement('div');
        searchLoading.classList.add('searcher__loading');

        searchLoading.innerText = 'Cargando..';
        container.appendChild(inputContainer);
        container.appendChild(searchResults);
        container.appendChild(searchLoading);

        return [container, inputContainer, searchResults, searchLoading];
    }

    private static _appendResult(product: Product, container: HTMLElement): void {
        const resultItem = document.createElement('div');
        resultItem.classList.add('searcher__result-item');

        const {
            title, shortDesc, path, colors, cover, type,
        } = product;

        const teaser = nodeTeaser(title, shortDesc, path, colors, cover, type, true);
        resultItem.appendChild(teaser);
        container.appendChild(resultItem);
    }

    private static _onDebouncedKeyUp(
        e: KeyboardEvent,
        searchResultsContainer: HTMLElement,
        searchLoadingContainer: HTMLElement,
    ): void {
        const { target } = e;

        if (target === null) {
            return;
        }

        const { value } = <HTMLInputElement>target;

        searchLoadingContainer.classList.add('searcher__loading--active');

        searchResultsContainer.innerHTML = '';

        if (value === '') {
            searchLoadingContainer.classList.remove('searcher__loading--active');
            return;
        }

        const cancelTokenSource = Searcher.cancelTokenSoruces.pop();

        if (cancelTokenSource) {
            cancelTokenSource.cancel();
        }

        Searcher.cancelTokenSoruces.push(
            API.generateCancelSource(),
        );

        API.findProducts(value).then((products) => {
            searchResultsContainer.innerHTML = '';

            for (const product of products) {
                this._appendResult(product, searchResultsContainer);
            }
        }).finally(() => {
            searchLoadingContainer.classList.remove('searcher__loading--active');
        });
    }

    public static _addEventListeners(
        input: HTMLInputElement,
        container: HTMLElement,
        searchResultsContainer: HTMLElement,
        searchLoading: HTMLElement,
    ): void {
        input.addEventListener('keyup', debounce((e) => {
            this._onDebouncedKeyUp(e, searchResultsContainer, searchLoading);
        }, 500));

        container.addEventListener('focusin', (e: FocusEvent) => {
            if (container.classList.contains('searcher--focused')) {
                return;
            }

            const overflowContainer = this._getOffsetParent(container, 1);
            const parent = this._getParentContainer(container, 1);

            if (overflowContainer === null) return;

            const y = overflowContainer.clientWidth - parent.clientWidth + container.clientWidth;

            const percentage = (y * 100) / container.clientWidth;

            container.style.width = `${percentage}%`;

            container.classList.add('searcher--focused');
        });

        container.addEventListener('focusout', (e: Event) => {
            /*
            This fixes some stupid navigator behaviour that prevents
            click events from triggering after the container has changed
            */
            if (container.matches(':hover')) {
                return;
            }

            container.style.width = '';
            container.classList.remove('searcher--focused');
        });
    }

    private static _getParentContainer(element: HTMLElement, skips = 0): HTMLElement {
        let ign = skips;

        if (element.parentElement === null) {
            return element;
        }

        if (ign === 0) {
            return element.parentElement;
        }

        return this._getParentContainer(element.parentElement, --ign);
    }

    private static _getOffsetParent(element: HTMLElement, skips = 0): HTMLElement {
        let ign = skips;

        const { parentElement } = element;

        if (parentElement === null) {
            return element;
        }

        const { position } = window.getComputedStyle(element);

        if (position === 'relative') {
            if (ign + 1 === 0) {
                return <HTMLElement>element;
            }

            ign--;
        }

        return this._getOffsetParent(<HTMLElement>parentElement, ign);
    }
}
